import { differenceInDays } from 'date-fns';
import _ from 'lodash';

import { getCountry } from '../../iwIntlProvider/CountryProvider';
import { getCurrentLocation } from '../../iwRouter/HistoryProvider';
import { getQueryParameter } from '../../iwRouter/LocationHelper';

export function deserialiseFundingRequirements(funding_requirements) {
    const deserialisedFundingRequirements = funding_requirements
        .filter(
            (requirement) =>
                requirement.category !== 'contractual' ||
                (requirement.category === 'contractual' &&
                    requirement.recommended_resolution_method ===
                        'complete_repayment_pledge')
        )
        .map((funding_requirement) => ({
            type: getActionFromFundingRequirement(funding_requirement),
            disabled: isCompleteActionDisabled(funding_requirement)
        }));

    const uniqueFundingRequirements =
        showUniqueFundingRequirementsWithCorrectStatus(
            deserialisedFundingRequirements
        );
    return _.orderBy(uniqueFundingRequirements, orderByPriority, 'asc');
}

function showUniqueFundingRequirementsWithCorrectStatus(fundingRequirements) {
    return fundingRequirements.reduce((requirements, requirement) => {
        const existingDuplicateRequirement = _.findIndex(requirements, {
            type: requirement.type
        });
        if (existingDuplicateRequirement === -1) {
            return [...requirements, requirement];
        }

        const STATUS_PRIORITY_ORDER = {
            REQUIRED: 1,
            ERROR: 2,
            COMPLETED: 3
        };
        if (
            STATUS_PRIORITY_ORDER[
                requirements[existingDuplicateRequirement].status
            ] > STATUS_PRIORITY_ORDER[requirement.status]
        ) {
            requirements[existingDuplicateRequirement] = requirement;
        }

        return requirements;
    }, []);
}

function orderByPriority(funding_requirement) {
    const PRIORITY_ORDER = {
        BANK_DETAILS: 1,
        PENNY_TRANSFER: 2,
        IDENTITY_QUESTION: 3,
        ONFIDO_VERIFICATION: 3,
        MAJOR_SHAREHOLDER_CHECK: 4,
        DEBIT_CARD: 5
    };
    const LOWEST_PRIORITY = 100;

    return PRIORITY_ORDER[funding_requirement.type] || LOWEST_PRIORITY;
}

function isCompleteActionDisabled(funding_requirement) {
    // TODO: Handle Pending Status once implemented on the endpoint
    return funding_requirement.status === 'completed';
}

const MAJOR_SHAREHOLDER_CHECK = 'MAJOR_SHAREHOLDER_CHECK';
function getActionFromFundingRequirement(funding_requirement) {
    switch (funding_requirement.recommended_resolution_method) {
        case 'add_default_payment_method':
            return 'DEBIT_CARD';
        case 'link_bank_account':
        case 'check_linked_direct_debit':
            return 'BANK_DETAILS';
        case 'complete_penny_transfer':
        case 'complete_penny_transfer_with_signature':
            return 'PENNY_TRANSFER';
        case 'equifax_eiv_check':
            return 'IDENTITY_QUESTION';
        case 'onfido_check':
            return 'ONFIDO_VERIFICATION';
        case 'onfido_video_check':
            return 'ONFIDO_VIDEO_VERIFICATION';
        case 'idverse_check':
            return 'IDVERSE_VERIFICATION';
        case 'major_shareholders_check':
            return MAJOR_SHAREHOLDER_CHECK;
        case 'check_customer_does_not_exceed_bip_cap':
            return 'BIP';
        default:
            return 'APPLICANT_CALL';
    }
}

export function getFundingPaymentOptions(paymentMethods) {
    const bankAccounts = _.chain(paymentMethods)
        .filter((payment) => payment.payment_method_type === 'bank_account')
        .map((payment) => {
            let bankDetailsNumber = null;
            const bankDetails = payment.bank_details;
            switch (getCountry()) {
                // for compliance has to be last 4 digits of IBAN, but whole IBAN is returned
                case 'DE':
                    bankDetailsNumber = bankDetails.iban.slice(-4);
                    break;
                case 'UK':
                default:
                    bankDetailsNumber = bankDetails.account_number;
                    break;
            }

            return {
                type: 'BANK_ACCOUNT',
                value: payment.payment_method_id,
                cardLastDigits: bankDetailsNumber,
                issuer: payment.issuer,
                brand: payment.brand,
                label: bankDetailsNumber,
                bankDetails: payment.bank_details
            };
        })
        .value();

    return {
        fundingPaymentOptions: bankAccounts,
        selectedFundingAccount: bankAccounts[0],
        selectedFunding: bankAccounts[0]
    };
}

export function getRepaymentOptions(paymentMethods) {
    const debitCards = _.chain(paymentMethods)
        .filter((payment) => payment.payment_method_type === 'card')
        .map((payment) => ({
            type: 'DEBIT_CARD',
            value: payment.payment_method_id,
            cardLastDigits: payment.last_digits,
            issuer: payment.issuer,
            brand: payment.brand,
            label: payment.last_digits
        }))
        .value();

    return {
        repaymentOptions: debitCards,
        selectedRepayment: debitCards[0]
    };
}

export function isInvalidAmount(drawDownAmount, minAmount, maxAmount) {
    return (
        (drawDownAmount && drawDownAmount < minAmount) ||
        drawDownAmount > maxAmount
    );
}

export function isInvalidDrawdownDate(drawDownDate, fundingDate) {
    const drawDownDateExpired =
        differenceInDays(new Date(), new Date(drawDownDate)) >= 7;

    return drawDownDate < fundingDate || drawDownDateExpired;
}

export function isEligibleForDrawDown(
    loanStatus,
    approvalStatus,
    hasExpiredApproval
) {
    return (
        loanStatus !== 'overdue' &&
        approvalStatus !== 'declined' &&
        !hasExpiredApproval
    );
}

export function getDrawdownAmountFromQueryParams() {
    const location = getCurrentLocation();
    return getQueryParameter(location, 'amount');
}

export function buildLocationWithSearch({
    pathname,
    payLinkId,
    orderId,
    offerId,
    productType,
    amount,
    sweep,
    accountId
}) {
    const params = new URLSearchParams();
    if (payLinkId) params.append('payLinkId', payLinkId);
    if (orderId) params.append('orderId', orderId);
    if (offerId) params.append('offerId', offerId);
    if (productType) params.append('productType', productType);
    if (amount !== undefined) params.append('amount', amount);
    if (sweep) params.append('sweep', sweep);
    if (accountId) params.append('accountId', accountId);

    const searchString = params.toString();
    return {
        pathname,
        search: searchString ? `?${searchString}` : undefined
    };
}
